/*
 * Ecranul 4a: cererile mele.
 *
 * De cand aprobarea s-a mutat pe tichet, ecranul asta e prima pagina pe care o
 * deschide cineva care astepta o aprobare in magazin si n-o mai gaseste. De-asta
 * spune de aici incolo, sub titlu, unde s-a mutat decizia; starea fiecarui
 * tichet in parte se vede in detaliu, pe articole.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../api/config';
import * as shop from '../api/shop';
import { Badge } from '../components/Badge';
import { Icon } from '../components/Icon';
import { hasNewReply } from '../lib/replies';
import { Pager } from '../components/Pager';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { EM_DASH, formatDateTime, labelState } from '../lib/format';
import { isDirectTicket, type RequestSummary } from '../api/types';
import { displayState } from '../lib/requestState';
import { ticketSubject } from '../lib/ticket';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';

/*
 * Filtrul de stare se face AICI, peste toata lista, nu in api.
 *
 * Pana acum filtrul trimitea `state` la `/shop/requests`, iar ruta nu-l citea:
 * orice alegere intorcea aceeasi lista. Si nici n-ar fi avut ce potrivi -
 * optiunile erau `state`-ul cererii (`submitted`, pentru totdeauna dupa
 * trimitere), iar eticheta din tabel arata starea tichetului („Pending
 * Approval"). Acum optiunile sunt chiar starile desenate in tabel, adunate din
 * toata lista, si se filtreaza dupa acelasi `displayState()` ca eticheta.
 */

/**
 * Titlul unei cereri: ce a cerut omul, nu numarul ei.
 *
 * Api-ul trimite numele primului articol (`title`) si cate sunt (`items_count`);
 * cu mai multe se scrie „Card issuing si inca 2". Fara nume - raspuns vechi,
 * sau cerere fara articole - ramane `null`, si randul cade pe „Cerere #N".
 */
function requestTitle(row: RequestSummary): string | null {
  const name = (row.title ?? '').trim();

  if (name === '') return null;

  const count = row.items_count ?? row.items?.length ?? 1;

  return count > 1 ? `${name} ${t('and {n} more').replace('{n}', String(count - 1))}` : name;
}

export function RequestsScreen() {
  const [state, setState] = useState('');
  const [page, setPage] = useState(1);

  /* Toata lista o data; filtrul si paginile se fac pe loc, fara alta cerere. */
  const all = useAsync((signal) => shop.allRequests({ signal }), []);

  const options = useMemo(() => {
    const seen = new Set<string>();
    for (const row of all.data ?? []) {
      const value = displayState(row);
      if (value) seen.add(value);
    }
    return [...seen];
  }, [all.data]);

  const view = useMemo(() => {
    if (!all.data) return null;

    const perPage = config.pageSize;
    const rows = state === '' ? all.data : all.data.filter((row) => displayState(row) === state);
    const last = Math.max(1, Math.ceil(rows.length / perPage));
    const current = Math.min(page, last);

    return {
      items: rows.slice((current - 1) * perPage, current * perPage),
      total: rows.length,
      page: current,
      per_page: perPage,
    };
  }, [all.data, state, page]);

  const list = { ...all, data: view };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Requests')}</p>
          <h1 className="page-title">
            {t('My requests')}
            {/*
              * Cate sunt — numarul din api, nu `items.length`.
              *
              * `items` e o singura pagina; numarat asa, cineva cu patruzeci de
              * cereri ar fi citit „25" si ar fi crezut ca aia sunt toate.
              *
              * Se deseneaza abia cand se stie, si numai peste zero: un „0"
              * aratat cat timp se incarca e o minciuna scurta, dar suficienta
              * cat sa inchida omul pagina.
              */}
            {list.status === 'ready' && (list.data?.total ?? 0) > 0 ? (
              <span className="count-chip">{list.data?.total}</span>
            ) : null}
          </h1>
          <p className="page-sub">
            {t(
              'Everything you have sent, newest first — from the catalog or straight to the service desk.',
            )}
          </p>
        </div>
        <Link className="btn btn--primary" to="/catalog">{t('New request')}</Link>
      </header>

      <div className="toolbar">
        <div className="field">
          <label className="field__label" htmlFor="requests-state">{t('Status')}</label>
          <select
            id="requests-state"
            className="field__control"
            value={state}
            onChange={(event) => {
              setState(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('All statuses')}</option>
            {options.map((value) => (
              <option key={value} value={value}>
                {labelState(value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {list.status === 'loading' ? <Loading label={t('Loading your requests…')} /> : null}
      {list.status === 'error' && list.error ? (
        <ErrorState error={list.error} onRetry={list.reload} />
      ) : null}

      {list.status === 'ready' && list.data ? (
        list.data.items.length === 0 ? (
          <EmptyState
            title={t('No requests')}
            text={
              state !== ''
                ? t('You have no requests with this status.') : t('You have not submitted any request yet.')
            }
            action={
              <Link className="btn btn--primary" to="/catalog">{t('Browse the catalog')}</Link>
            }
          />
        ) : (
          <>
            <section className="panel panel--flush">
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('Request')}</th>
                      <th scope="col">{t('Submitted')}</th>
                      <th scope="col" className="num-col">{t('Items')}</th>
                      <th scope="col">{t('Status')}</th>
                      <th scope="col">{t('Last update')}</th>
                      {/* Fara nume: coloana nu poarta o informatie, ci semnul ca
                          randul duce undeva. Un titlu i-ar fi promis un continut. */}
                      <th scope="col" className="go-col"><span className="sr-only">{t('Open')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.items.map((row) =>
                      /*
                       * Doua feluri de rand in aceeasi lista, deosebite dupa
                       * `kind` - nu dupa "are articole sau nu". Id-urile celor
                       * doua nu vin din acelasi sir, deci fara `kind` ar fi doua
                       * lucruri diferite sub acelasi numar, si legatura ar duce
                       * la fisa gresita.
                       */
                      isDirectTicket(row) ? (
                        <tr key={`t${row.id}`}>
                          <td>
                            {/*
                              * Intai ce a scris omul, apoi numarul.
                              *
                              * „Tichet #155" nu spune nimic celui care cauta in
                              * lista lui: numerele nu se tin minte, subiectele
                              * da. Numarul ramane dedesubt, fiindca ala e ce
                              * spune la telefon cand suna la service desk.
                              *
                              * Cand subiectul lipseste, numarul urca la loc pe
                              * primul rand: un rand fara titlu n-ar mai fi avut
                              * pe ce se apasa.
                              */}
                            {/* Fara „IT Service Catalog Request: " in fata -
                                vezi ticketSubject(). */}
                            <Link className="table__strong" to={`/tickets/${row.id}`}>
                              {ticketSubject(row.subject) || `${t('Ticket')} #${row.id}`}
                            </Link>
                            {/*
                              * Clopotelul, pe randul unde chiar ti-a scris cineva.
                              *
                              * Numarul din meniu spune CATE sunt; aici se vede
                              * PE CARE. Fara el, omul care vede „2" in meniu
                              * deschide randurile pe rand pana le gaseste.
                              *
                              * Aceeasi culoare si acelasi semn ca in meniu si ca
                              * pe fisa tichetului: sunt acelasi lucru, urmarit
                              * din trei locuri.
                              */}
                            {hasNewReply(row.id, row.last_staff_comment_id) ? (
                              <span className="title-bell" title={t('The service desk has replied')}>
                                <Icon name="bell" />
                                <span className="sr-only">{t('The service desk has replied')}</span>
                              </span>
                            ) : null}
                            {ticketSubject(row.subject) ? (
                              <span className="table__sub">{t('Ticket')} #{row.id}</span>
                            ) : null}
                          </td>
                          <td className="nowrap">{formatDateTime(row.submitted_at)}</td>
                          {/* Un tichet direct n-are articole. Liniuta, nu zero:
                              zero ar fi spus "am cerut, dar nimic" - altceva. */}
                          <td className="num-col num">{EM_DASH}</td>
                          <td><Badge state={row.status ?? undefined} /></td>
                          <td className="nowrap table__muted">
                            {row.updated_at ? formatDateTime(row.updated_at) : EM_DASH}
                          </td>
                          <td className="go-col"><Icon name="chevron" /></td>
                        </tr>
                      ) : (
                        <tr key={`r${row.id}`}>
                          <td>
                            {/* Prin `t()`, ca perechea lui de deasupra. Scris
                                direct in markup, arata „Request #88" langa
                                „Tichet #5512" in aceeasi lista romaneasca -
                                se vedea abia de cand stau una langa alta. */}
                            {/*
                              * Ce s-a cerut, apoi numarul - ca la tichete.
                              *
                              * Cu un singur articol, si tichetul lui langa
                              * numar: service desk-ul vorbeste despre „tichetul
                              * 314", omul a trimis „cererea 75", si fara legatura
                              * scrisa aici par doua lucruri diferite.
                              */}
                            <Link className="table__strong" to={`/requests/${row.id}`}>
                              {requestTitle(row) ?? `${t('Request')} #${row.id}`}
                            </Link>
                            {requestTitle(row) !== null || row.ticket_id ? (
                              <span className="table__sub">
                                {t('Request')} #{row.id}
                                {row.ticket_id ? ` · ${t('Ticket')} #${row.ticket_id}` : null}
                              </span>
                            ) : null}
                          </td>
                          <td className="nowrap">
                            {formatDateTime(row.created_at ?? row.submitted_at)}
                          </td>
                          <td className="num-col num">
                            {row.items_count ?? row.items?.length ?? '—'}
                          </td>
                          <td>
                            {/* Starea tichetului cand cererea are un singur articol, altfel a
                                cererii. Vezi Views::request() in api: acolo se
                                hotaraste, aici doar se prefera ce a venit. */}
                            <Badge state={row.ticket_state ?? row.state ?? row.status} />
                          </td>
                          <td className="nowrap table__muted">
                            {/* Cand s-a miscat ultima data un tichet al cererii.
                                `submitted_at` nu se mai schimba dupa trimitere, deci
                                o cerere lucrata azi arata acolo la fel cu una uitata
                                de doua saptamani. Liniuta cat timp niciun tichet
                                n-a fost deschis inca. */}
                            {row.ticket_updated ? formatDateTime(row.ticket_updated) : EM_DASH}
                          </td>
                          <td className="go-col"><Icon name="chevron" /></td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            <Pager
              total={list.data.total}
              page={list.data.page}
              perPage={list.data.per_page}
              unit={['request', 'requests']}
              onPage={setPage}
            />
          </>
        )
      ) : null}
    </>
  );
}
